import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { CreditCard, Loader2, X } from 'lucide-react';
import {
  IssueCardModal,
  ISSUE_CARD_MODAL_OPEN_EVENT,
  encodeErrorKey,
  encodeCard,
  encodeRequestFromPayload,
  chipTypeForCarrier,
  formatApiError,
  getApiClient,
  isApiRequestError,
  PERMISSIONS,
  useNfcAgent,
  usePermissions,
  useTranslation,
} from '@bms/common';
import type { AccessCard, AgentWriteReport, ChipType, ReaderCardInfo } from '@bms/common';
import { toast } from 'sonner';
import { useCardTapOpenSearch } from '@/lib/cardTapPreferences';
import { openDeskSearch } from '@/components/search/deskSearchStore';
import { cardStatusWord, cardTypeLabel } from '@/components/search/deskResults';
import { CheckInDialog } from '@/components/checkin/CheckInDialog';

type CardTapToast = {
  title: string;
  description: string;
  actionLabel?: string;
  onAction?: () => void;
  /** A second, quieter choice beside the primary one. */
  secondaryLabel?: string;
  onSecondary?: () => void;
  busy?: boolean;
  tone?: 'default' | 'success' | 'warning' | 'error';
};

type PendingIssueCard = {
  cardUid: string;
  chipType?: ChipType;
};

type IssueCardModalWindow = Window & { __bmsIssueCardModalCount?: number };

/**
 * A tap failure in words: a REST refusal as a translated sentence (never the
 * server's text, UX-30); the NFC agent's own reason as it gave it.
 */
function tapErrorText(error: unknown, fallback: string, t: (key: string) => string): string {
  if (isApiRequestError(error)) return formatApiError(error, t, { serverMessage: false });
  return error instanceof Error ? error.message : fallback;
}

function isHttpStatus(error: unknown, status: number): boolean {
  return (error as { response?: { status?: number } } | null)?.response?.status === status;
}

export function CardTapHandler() {
  const location = useLocation();
  const { t } = useTranslation();
  // The server's projection for this site decides who may issue (D4); a role
  // name never does. The server also lets `operator` and `front_office_manager`
  // issue guest cards (`canIssueCardType`), and both hold `cards:write`.
  const permissions = usePermissions();
  const canIssueCards = permissions.has(PERMISSIONS.CARDS_WRITE);
  // The walk-in ends in a check-in, which `PATCH …/occupancy` refuses without
  // `grms:write`; without it the blank card is only offered for issuing.
  const canCheckIn = permissions.has(PERMISSIONS.GRMS_WRITE);
  const agent = useNfcAgent();
  const { enabled: openSearchOnTap } = useCardTapOpenSearch();

  const [issueCard, setIssueCard] = useState<PendingIssueCard | null>(null);
  const [issueDialogOpen, setIssueDialogOpen] = useState(false);
  const [walkInOpen, setWalkInOpen] = useState(false);
  const processedTapKeyRef = useRef<string | null>(null);
  const lastRouteChangeRef = useRef(0);
  const firstLocationRef = useRef(true);
  const toastIdRef = useRef<string | number | null>(null);
  // The room dialog is open on whichever route hosts the board (`/` and
  // `/dashboard` both do), and it shows the card itself: no toast on top of it.
  const suppressTapToasts = useMemo(
    () => new URLSearchParams(location.search).has('room'),
    [location.search],
  );

  useEffect(() => {
    const readIssueDialogOpen = () => ((window as IssueCardModalWindow).__bmsIssueCardModalCount ?? 0) > 0;
    setIssueDialogOpen(readIssueDialogOpen());

    const handleIssueDialogChange = (event: Event) => {
      const nextOpen = (event as CustomEvent<{ open?: boolean }>).detail?.open ?? readIssueDialogOpen();
      setIssueDialogOpen(nextOpen);
      if (nextOpen && toastIdRef.current != null) {
        toast.dismiss(toastIdRef.current);
        toastIdRef.current = null;
      }
    };

    window.addEventListener(ISSUE_CARD_MODAL_OPEN_EVENT, handleIssueDialogChange);
    return () => window.removeEventListener(ISSUE_CARD_MODAL_OPEN_EVENT, handleIssueDialogChange);
  }, []);

  useEffect(() => {
    if (firstLocationRef.current) {
      firstLocationRef.current = false;
      return;
    }
    lastRouteChangeRef.current = Date.now();
  }, [location.key]);

  const dismissToast = useCallback(() => {
    if (toastIdRef.current != null) toast.dismiss(toastIdRef.current);
    toastIdRef.current = null;
  }, []);

  const showToast = useCallback((next: CardTapToast) => {
    dismissToast();
    toastIdRef.current = toast.custom((id) => (
      <div className="w-[min(calc(100vw-2rem),24rem)] rounded-lg border bg-popover p-4 text-popover-foreground shadow-lg">
        <div className="flex items-start gap-3">
          <div className={[
            'mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-muted text-muted-foreground',
            next.tone === 'success' && 'bg-success/15 text-success',
            next.tone === 'warning' && 'bg-warning/15 text-warning',
            next.tone === 'error' && 'bg-destructive/15 text-destructive',
          ].filter(Boolean).join(' ')}>
            {next.busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <CreditCard className="h-4 w-4" />}
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-title font-semibold leading-tight">{next.title}</p>
            <p className="mt-1 text-caption leading-5 text-muted-foreground">{next.description}</p>
            {next.actionLabel && next.onAction && (
              <div className="mt-3 flex flex-wrap gap-2">
                <button
                  type="button"
                  className="h-8 rounded-md bg-primary px-3 text-caption font-medium text-primary-foreground"
                  onClick={() => {
                    next.onAction?.();
                    toast.dismiss(id);
                    if (toastIdRef.current === id) toastIdRef.current = null;
                  }}
                  disabled={next.busy}
                >
                  {next.actionLabel}
                </button>
                {next.secondaryLabel && next.onSecondary && (
                  <button
                    type="button"
                    className="h-8 rounded-md border px-3 text-caption font-medium"
                    onClick={() => {
                      next.onSecondary?.();
                      toast.dismiss(id);
                      if (toastIdRef.current === id) toastIdRef.current = null;
                    }}
                    disabled={next.busy}
                  >
                    {next.secondaryLabel}
                  </button>
                )}
              </div>
            )}
          </div>
          <button
            type="button"
            className="rounded-md p-1 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
            onClick={() => {
              toast.dismiss(id);
              if (toastIdRef.current === id) toastIdRef.current = null;
            }}
            aria-label={t('shell.cardTap.dismissAria')}
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>
    ), { duration: next.busy ? Infinity : 8000 });
  }, [dismissToast, t]);

  const openStandaloneIssuer = useCallback((cardUid: string, chipType?: ChipType) => {
    dismissToast();
    setIssueCard({ cardUid, chipType });
  }, [dismissToast]);

  const confirmPendingWrite = useCallback(async function retryWriteConfirmation(cardId: string, writeReport: AgentWriteReport) {
    try {
      await getApiClient().confirmCardWritten(cardId, writeReport);
      showToast(writeReport.outcome === 'unknown'
        ? {
          title: t('shell.cardTap.writeNotConfirmedTitle'),
          description: t('shell.cardTap.writeNotConfirmedDescription', { uid: writeReport.cardUid }),
          tone: 'warning',
        }
        : {
          title: t('shell.cardTap.cardEncodedTitle'),
          description: t('shell.cardTap.cardEncodedDescription', { uid: writeReport.cardUid }),
          tone: 'success',
        });
    } catch (error) {
      showToast({
        title: t('shell.cardTap.writeFailedTitle'),
        description: t('shell.cardTap.writeFailedDescription', {
          error: tapErrorText(error, t('shell.cardTap.writeFailedDefaultError'), t),
        }),
        tone: 'error',
        actionLabel: t('shell.cardTap.retrySave'),
        onAction: () => { void retryWriteConfirmation(cardId, writeReport); },
      });
    }
  }, [showToast, t]);

  const encodePendingCard = useCallback(async (
    cardId: string,
    cardUid: string,
    readerId: string,
    chipType: ChipType,
  ) => {
    showToast({
      title: t('shell.cardTap.encodingTitle'),
      description: t('shell.cardTap.encodingDescription', { uid: cardUid }),
      busy: true,
    });
    try {
      const payload = await getApiClient().getCardPayload(cardId, chipType);
      const encoded = await encodeCard(readerId, encodeRequestFromPayload(payload), { claimId: agent.claimId });
      // An `unknown` outcome may have written the card: record it so Edge keeps the
      // issuance, and the card stays `pending`. BMS-spec docs/access/cards.md §Write report.
      const outcomeUnknown = encoded.writeReport?.outcome === 'unknown';
      if (!outcomeUnknown && !encoded.success) {
        showToast({
          title: t('shell.cardTap.encodeFailedTitle'),
          description: encoded.error ? t(encodeErrorKey(encoded.error)) : t('shell.cardTap.encodeFailedReaderError'),
          tone: 'error',
        });
        return;
      }
      if (!encoded.writeReport || (!outcomeUnknown && encoded.writeReport.outcome !== 'written')) {
        showToast({
          title: t('shell.cardTap.confirmMissingTitle'),
          description: t('shell.cardTap.confirmMissingDescription'),
          tone: 'error',
        });
        return;
      }
      await confirmPendingWrite(cardId, encoded.writeReport);
    } catch (error) {
      showToast({
        title: t('shell.cardTap.encodeFailedTitle'),
        description: tapErrorText(error, t('shell.cardTap.encodeFailedPayloadError'), t),
        tone: 'error',
      });
    }
  }, [agent.claimId, confirmPendingWrite, showToast, t]);

  // A card the site knows opens the desk palette on it (P5): its holder, its
  // status and validity in words, and the stay or room it opens. With "open
  // search on tap" off the toast says the same and offers the palette.
  const presentKnownCard = useCallback((card: AccessCard) => {
    if (openSearchOnTap) {
      dismissToast();
      openDeskSearch({ card });
      return;
    }
    showToast({
      title: t('shell.cardTap.cardRecognizedTitle'),
      description: t('shell.cardTap.cardSentence', {
        type: cardTypeLabel(t, card.cardType),
        uid: card.cardUid,
        status: cardStatusWord(t, card.effectiveStatus),
      }),
      actionLabel: t('shell.cardTap.showInSearch'),
      onAction: () => openDeskSearch({ card }),
      tone: card.effectiveStatus === 'active' ? 'default' : 'warning',
    });
  }, [dismissToast, openSearchOnTap, showToast, t]);

  const handleTap = useCallback(async (readerId: string, card: ReaderCardInfo) => {
    const cardUid = card.uid;
    if (!cardUid) return;
    const chipType = chipTypeForCarrier(card.carrier);

    try {
      const existing = await getApiClient().getAccessCardByUid(cardUid);
      if (existing.status === 'pending') {
        if (!chipType) {
          showToast({
            title: t('shell.cardTap.unsupportedCardTitle'),
            description: t('shell.cardTap.unsupportedCardDescription'),
            tone: 'error',
          });
          return;
        }
        showToast({
          title: t('shell.cardTap.pendingCardTitle'),
          description: t('shell.cardTap.pendingCardDescription'),
          actionLabel: t('shell.cardTap.encodeCard'),
          onAction: () => void encodePendingCard(existing.id, cardUid, readerId, chipType),
          tone: 'warning',
        });
        return;
      }

      presentKnownCard(existing);
    } catch (error) {
      if (!isHttpStatus(error, 404)) {
        showToast({
          title: t('shell.cardTap.cardLookupFailedTitle'),
          description: tapErrorText(error, t('shell.cardTap.cardLookupFailedDefault'), t),
          tone: 'error',
        });
        return;
      }

      if (!canIssueCards) {
        showToast({
          title: t('shell.cardTap.unknownCardTitle'),
          description: t('shell.cardTap.unknownCardNeedsManager'),
          tone: 'warning',
        });
        return;
      }

      if (!canCheckIn) {
        showToast({
          title: t('shell.cardTap.unknownCardTitle'),
          description: t('shell.cardTap.unknownCardIssuePrompt'),
          actionLabel: t('shell.cardTap.issueThisCard'),
          onAction: () => openStandaloneIssuer(cardUid, chipType),
          tone: 'warning',
        });
        return;
      }

      // Walk-in (Idea 3, D4): a blank card at the desk is most often a new
      // guest's key. The card stays on the reader, so the check-in's key step
      // writes it as key 1. The second choice is an extra key for a room (F14):
      // the issuer opens on its room search with this card preselected; locks,
      // lock groups and roles are not named at the desk.
      showToast({
        title: t('shell.cardTap.unknownCardTitle'),
        description: t('shell.cardTap.unknownCardWalkInPrompt'),
        actionLabel: t('shell.cardTap.walkIn'),
        onAction: () => { dismissToast(); setWalkInOpen(true); },
        secondaryLabel: t('shell.cardTap.extraKey'),
        onSecondary: () => openStandaloneIssuer(cardUid, chipType),
        tone: 'warning',
      });
    }
  }, [canCheckIn, canIssueCards, dismissToast, encodePendingCard, openStandaloneIssuer, presentKnownCard, showToast, t]);

  useEffect(() => {
    const tap = agent.lastCard;
    if (!tap?.card.uid) {
      processedTapKeyRef.current = null;
      dismissToast();
      return;
    }

    const tapKey = `${tap.readerId}:${tap.card.uid}`;
    if (suppressTapToasts) {
      processedTapKeyRef.current = tapKey;
      dismissToast();
      return;
    }

    if (issueDialogOpen) {
      processedTapKeyRef.current = tapKey;
      dismissToast();
      return;
    }

    if (processedTapKeyRef.current === tapKey) return;
    processedTapKeyRef.current = tapKey;

    if (lastRouteChangeRef.current > 0 && Date.now() - lastRouteChangeRef.current < 2000) {
      return;
    }

    void handleTap(tap.readerId, tap.card);
  }, [agent.lastCard, dismissToast, handleTap, issueDialogOpen, suppressTapToasts]);

  return (
    <>
      {walkInOpen && (
        <CheckInDialog
          open={walkInOpen}
          onOpenChange={setWalkInOpen}
        />
      )}
      {issueCard && (
        <IssueCardModal
          open={!!issueCard}
          onClose={() => setIssueCard(null)}
          mode="standalone"
          context={{ defaultCardUid: issueCard.cardUid, defaultChipType: issueCard.chipType }}
          onIssued={(card) => {
            setIssueCard(null);
            showToast({
              title: t('shell.cardTap.cardIssuedTitle'),
              description: t('shell.cardTap.cardIssuedDescription', { uid: card.cardUid }),
              tone: 'success',
            });
          }}
          onNotify={({ level, message }) => {
            if (level === 'success') toast.success(message);
            else if (level === 'warning') toast.warning(message);
            else toast.error(message);
          }}
        />
      )}
    </>
  );
}
